/**
 * Avatar component with initials fallback and depth effect.
 *
 * Displays user avatar as an image or initials with a subtle
 * white inset border for visual depth.
 */

import * as stylex from "@stylexjs/stylex";

import { colors, fontSize, fontWeight, radius } from "../tokens.stylex";

const SIZES = {
  sm: 24,
  md: 32,
  lg: 40,
  xl: 56,
} as const;

const FONT_SIZES = {
  sm: fontSize["2xs"],
  md: fontSize.xs,
  lg: fontSize.sm,
  xl: fontSize.md,
} as const;

const styles = stylex.create({
  base: {
    borderRadius: radius.full,
    flexShrink: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontWeight: fontWeight.semibold,
    color: colors.primaryForeground,
    backgroundColor: colors.primary,
    position: "relative",
    overflow: "hidden",
  },
  ring: {
    position: "absolute",
    inset: 0,
    borderRadius: radius.full,
    borderWidth: "1.5px",
    borderStyle: "solid",
    borderColor: colors.avatarInset,
    pointerEvents: "none",
  },
  image: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
  },
});

export type AvatarSize = keyof typeof SIZES;

export interface AvatarProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, "children"> {
  /** User's display name (used for initials fallback) */
  name: string;
  /** Optional image URL */
  src?: string;
  /** Size variant */
  size?: AvatarSize;
  /** Additional stylex styles */
  xstyle?: stylex.StyleXStyles;
}

function getInitials(name: string): string {
  return name
    .split(" ")
    .map((part) => part[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase();
}

export function Avatar({
  name,
  src,
  size = "md",
  xstyle,
  ...props
}: AvatarProps) {
  const initials = getInitials(name);
  const dimension = SIZES[size];
  const fontSizeValue = FONT_SIZES[size];

  return (
    <div
      {...stylex.props(styles.base, xstyle)}
      style={{ width: dimension, height: dimension, fontSize: fontSizeValue }}
      {...props}
    >
      {src ? (
        <img
          alt={name}
          height={dimension}
          src={src}
          width={dimension}
          {...stylex.props(styles.image)}
        />
      ) : (
        initials
      )}
      <div {...stylex.props(styles.ring)} />
    </div>
  );
}

export const avatar = styles;
